import { CONSOLE_PATHS, consoleRoutes } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { ErrorState, Skeleton } from "@sagot/ui";
import type { ReactNode } from "react";
import { Navigate, Outlet, useLocation, useSearchParams } from "react-router";
import { readLastWorkspace } from "./last-workspace";
import { readPendingAuth, safeNext } from "./pending-auth";
import { useBootState, useSession } from "./session-provider";
import { useMe } from "./use-me";

/** Full-page skeleton while the session or memberships load (no spinners for content). */
export function PageSkeleton() {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen bg-canvas lg:pl-64" aria-busy="true">
      <span className="sr-only" role="status">
        {t("console.loadingSession")}
      </span>
      <div className="fixed inset-y-0 left-0 hidden w-64 border-r border-chrome-line bg-chrome lg:block" />
      <div className="h-14 border-b border-line bg-surface" />
      <div className="mx-auto max-w-7xl space-y-6 px-4 py-6 lg:px-8 lg:py-8">
        <Skeleton className="h-8 w-[240px]" />
        <Skeleton className="h-4 w-[420px] max-w-full" />
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <Skeleton className="h-28" />
          <Skeleton className="h-28" />
          <Skeleton className="h-28" />
        </div>
        <Skeleton className="h-64" />
      </div>
    </div>
  );
}

function BootError() {
  const { t } = useTranslation();
  const { retry } = useBootState();
  return (
    <main className="flex min-h-screen items-center justify-center bg-canvas px-4">
      <div className="card w-full max-w-md">
        <ErrorState title={t("console.sessionErrorTitle")} description={t("console.sessionErrorBody")} onRetry={retry} retryLabel={t("common.retry")} headingLevel={2} />
      </div>
    </main>
  );
}

/** Where an anonymous visitor goes: a pending 2FA / verification step, else sign-in with `?next=`. */
function signInTarget(pathname: string, search: string): string {
  const pending = readPendingAuth();
  if (pending?.kind === "two-factor") return CONSOLE_PATHS.twoFactor;
  if (pending?.kind === "verify-email") return `${CONSOLE_PATHS.verifyEmail}?email=${encodeURIComponent(pending.email)}`;
  const next = `${pathname}${search}`;
  return next === "/" ? CONSOLE_PATHS.login : `${CONSOLE_PATHS.login}?next=${encodeURIComponent(next)}`;
}

/** Signed-in routes. Unknown session → skeleton; anonymous → the right auth step. */
export function RequireAuth({ children }: { children?: ReactNode }) {
  const session = useSession();
  const boot = useBootState();
  const location = useLocation();
  if (boot.failed) return <BootError />;
  if (session.status === "unknown") return <PageSkeleton />;
  if (session.status === "anonymous") return <Navigate to={signInTarget(location.pathname, location.search)} replace />;
  return children ?? <Outlet />;
}

/** Sign-in / sign-up pages: a signed-in user continues to `?next=` (or home). */
export function PublicOnly({ children }: { children?: ReactNode }) {
  const session = useSession();
  const [params] = useSearchParams();
  if (session.status === "authenticated") return <Navigate to={safeNext(params.get("next")) ?? "/"} replace />;
  return children ?? <Outlet />;
}

/** `/`: last-used workspace dashboard, else the first one, else onboarding. */
export function HomeRedirect() {
  const me = useMe();
  const { t } = useTranslation();
  if (me.isPending) return <PageSkeleton />;
  if (me.isError) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-canvas px-4">
        <div className="card w-full max-w-md">
          <ErrorState title={t("console.sessionErrorTitle")} description={t("console.sessionErrorBody")} onRetry={() => void me.refetch()} retryLabel={t("common.retry")} />
        </div>
      </main>
    );
  }
  const workspaces = me.data.workspaces.filter((w) => w.membership_status === "active");
  const last = readLastWorkspace();
  const target = workspaces.find((w) => w.slug === last) ?? workspaces[0];
  return <Navigate to={target ? consoleRoutes.dashboard(target.slug) : CONSOLE_PATHS.onboarding} replace />;
}
